/**
 * TicketTypeChooser — Layer 1 of the type-differentiation system.
 *
 * Two large tiles + keyboard accelerators. Forces the agent to commit to
 * Inbound vs Service Call BEFORE the form opens. Smart-default highlights
 * the tile matching the user's workstation_kind tag.
 */
import { useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { TICKET_TYPES, defaultTypeForUser } from "@/lib/ticketType";
import { useAuth } from "@/context/AuthContext";

export default function TicketTypeChooser({ open, onOpenChange, onPicked }) {
  const { user } = useAuth();
  const smart = useMemo(() => defaultTypeForUser(user), [user]);

  // Keyboard accelerators: I = Inbound, S = Service Call
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      const k = (e.key || "").toLowerCase();
      if (k === "i") { e.preventDefault(); onPicked("Inbound"); }
      else if (k === "s") { e.preventDefault(); onPicked("Service Call"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onPicked]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="ticket-type-chooser">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl text-slate-900">
            What kind of ticket are you creating?
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">
            Pick a type to start. <kbd className="px-1 py-0.5 border border-slate-200 rounded text-[10px] font-mono bg-slate-50">I</kbd> or <kbd className="px-1 py-0.5 border border-slate-200 rounded text-[10px] font-mono bg-slate-50">S</kbd> on your keyboard.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-6">
          {Object.values(TICKET_TYPES).map((t) => {
            const Icon = t.icon;
            const isSmart = smart === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onPicked(t.label)}
                data-testid={`type-tile-${t.key}`}
                className={`group relative text-left rounded-xl border-2 transition-all p-5 hover:shadow-md ${t.softBg} ${t.softBorder} hover:${t.border}`}
              >
                {isSmart && (
                  <Badge variant="outline" className={`absolute top-3 right-3 text-[9px] ${t.pill}`}>
                    Recommended for you
                  </Badge>
                )}
                <div className={`w-12 h-12 rounded-lg ${t.softBg} border ${t.softBorder} flex items-center justify-center mb-3 relative`}>
                  <Icon className={`w-6 h-6 ${t.iconColor}`} strokeWidth={2} />
                  {t.pulse && (
                    <span className={`absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-500 animate-pulse`} />
                  )}
                </div>
                <div className="font-heading text-base font-semibold text-slate-900 uppercase tracking-wider">
                  {t.label}
                </div>
                <p className="text-[11px] text-slate-600 mt-1 leading-snug">{t.description}</p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200/70">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                    {t.code_prefix}-####
                  </span>
                  <kbd className="px-1.5 py-0.5 border border-slate-200 rounded text-[10px] font-mono bg-white text-slate-500 group-hover:bg-slate-50">
                    press {t.accelKey.toUpperCase()}
                  </kbd>
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-6 pb-5 -mt-2 text-center">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="type-chooser-cancel">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
